import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { LoadingIndicator } from 'atoms';
import { PopupNames, popupState } from 'core';
import { ProvisionDocOverrideInput } from 'core/graphql';
import {
  useProvisioningFolders,
  useProvisionStudents,
  usePopupStateOperations,
  useToasts,
  ProvisioningStudentDocRow,
} from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import { PATHS } from 'router/config';

import * as S from './PandaDocProvisioning.styled';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from './constants';
import { ProvisioningResult } from './ProvisioningResult';

interface IDraftRow {
  selected: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  phone: string;
}

type DraftMap = Record<string, IDraftRow>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const docId = (doc: ProvisioningStudentDocRow): string => doc.pandadocId ?? '';

/**
 * Phone numbers without a country code fail backend validation. The LMS is
 * Polish and all PandaDoc data uses +48 — prepend it when the `+` is missing.
 */
const normalizePhone = (raw: string): string => {
  const phone = raw.trim();
  if (!phone || phone.startsWith('+')) {
    return phone;
  }
  return `+48${phone}`;
};

/** Builds the editable draft state from the documents returned by the API. */
const buildDrafts = (docs: ProvisioningStudentDocRow[]): DraftMap =>
  docs.reduce<DraftMap>((acc, doc) => {
    acc[docId(doc)] = {
      // Already-provisioned rows start deselected (and stay locked).
      selected: !doc.alreadyProvisioned,
      firstName: doc.firstName ?? '',
      lastName: doc.lastName ?? '',
      middleName: '',
      email: doc.email ?? '',
      phone: doc.phone ?? '',
    };
    return acc;
  }, {});

/** Screen «Папка» — review documents and provision students (§4.2–4.3). */
export const FolderScreen = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const { folderUuid } = useParams<{ folderUuid: string }>();

  const {
    folders,
    loading,
    fetching,
    permissionDenied,
    refetch,
  } = useProvisioningFolders();
  const folder = folders.find((item) => item.folderUuid === folderUuid);
  const docs = useMemo<ProvisioningStudentDocRow[]>(
    () =>
      (folder?.studentDocs ?? []).filter(
        Boolean,
      ) as ProvisioningStudentDocRow[],
    [folder],
  );

  const [
    provision,
    { results, topLevelErrors, loading: provisioning },
  ] = useProvisionStudents();
  const { addToast } = useToasts();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const [drafts, setDrafts] = useState<DraftMap>({});
  const [contributionAmount, setContributionAmount] = useState('');
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showInvalid, setShowInvalid] = useState(false);

  // Re-seed drafts only when the document set actually changes (e.g. after a
  // refetch flips `alreadyProvisioned`) — a benign network echo keeps edits.
  const docsSignature = docs
    .map((doc) => `${docId(doc)}:${doc.alreadyProvisioned ? 1 : 0}`)
    .join('|');
  useEffect(() => {
    setDrafts(buildDrafts(docs));
    setShowInvalid(false);
    setValidationErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docsSignature]);

  // Refetch folders when the group-creation popup closes — a freshly created
  // group makes `existingGroupId` appear on the folder (tz-backend.md §3.3).
  const addGroupOpen = useReactiveVar(popupState).addGroup;
  const wasAddGroupOpen = useRef(false);
  useEffect(() => {
    if (wasAddGroupOpen.current && !addGroupOpen) {
      refetch();
    }
    wasAddGroupOpen.current = addGroupOpen;
  }, [addGroupOpen, refetch]);

  // After a provisioning run: surface a summary toast and refetch the folder.
  const handledRef = useRef<unknown>(null);
  useEffect(() => {
    if (results && results !== handledRef.current) {
      handledRef.current = results;
      const ok = results.filter((item) => item.success).length;
      const failed = results.filter((item) => !item.success && !item.skipped)
        .length;
      addToast({
        data: {
          title: failed
            ? t('pages.pandadocProvisioning.toasts.provisionedWithErrors', {
                ok,
                failed,
              })
            : t('pages.pandadocProvisioning.toasts.provisioned', { ok }),
          type: failed ? ToastTypes.warning : ToastTypes.success,
        },
        type: GlobalToastTypeNames.Provisioning,
      });
      refetch();
    }
  }, [results, addToast, refetch, t]);

  useEffect(() => {
    if (topLevelErrors && topLevelErrors !== handledRef.current) {
      handledRef.current = topLevelErrors;
      addToast({
        data: {
          title: t('pages.pandadocProvisioning.toasts.provisionFailed'),
          type: ToastTypes.error,
        },
        type: GlobalToastTypeNames.Provisioning,
      });
      refetch();
    }
  }, [topLevelErrors, addToast, refetch, t]);

  const goBack = useCallback(() => {
    history.push(PATHS.debtorsPandadocProvisioning);
  }, [history]);

  const patchDraft = useCallback((id: string, patch: Partial<IDraftRow>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const selectedDocs = docs.filter((doc) => drafts[docId(doc)]?.selected);

  const invalidPhoneIds = useMemo(() => {
    const ids = new Set<string>();
    selectedDocs.forEach((doc) => {
      if (!drafts[docId(doc)]?.phone.trim()) {
        ids.add(docId(doc));
      }
    });
    return ids;
  }, [selectedDocs, drafts]);

  const invalidEmailIds = useMemo(() => {
    const ids = new Set<string>();
    selectedDocs.forEach((doc) => {
      const email = drafts[docId(doc)]?.email.trim() ?? '';
      if (!EMAIL_REGEX.test(email)) {
        ids.add(docId(doc));
      }
    });
    return ids;
  }, [selectedDocs, drafts]);

  const invalidNameIds = useMemo(() => {
    const ids = new Set<string>();
    selectedDocs.forEach((doc) => {
      const draft = drafts[docId(doc)];
      if (!draft?.firstName.trim() || !draft?.lastName.trim()) {
        ids.add(docId(doc));
      }
    });
    return ids;
  }, [selectedDocs, drafts]);

  const validate = useCallback((): string[] => {
    const errors: string[] = [];
    if (!selectedDocs.length) {
      errors.push(t('pages.pandadocProvisioning.errors.selectAtLeastOne'));
    }
    if (invalidNameIds.size) {
      errors.push(t('pages.pandadocProvisioning.errors.nameMissing'));
    }
    if (invalidPhoneIds.size) {
      errors.push(t('pages.pandadocProvisioning.errors.phoneMissing'));
    }
    if (invalidEmailIds.size) {
      errors.push(t('pages.pandadocProvisioning.errors.emailInvalid'));
    }
    const amount = Number(contributionAmount);
    if (!contributionAmount || Number.isNaN(amount) || amount <= 0) {
      errors.push(t('pages.pandadocProvisioning.errors.amountInvalid'));
    }
    if (
      !CURRENCY_OPTIONS.includes(currency as typeof CURRENCY_OPTIONS[number])
    ) {
      errors.push(t('pages.pandadocProvisioning.errors.currencyMissing'));
    }
    return errors;
  }, [
    selectedDocs,
    invalidNameIds,
    invalidPhoneIds,
    invalidEmailIds,
    contributionAmount,
    currency,
    t,
  ]);

  // Builds the mutation payload from selected rows: only fields the manager
  // actually changed, plus the phone (required) when present.
  const buildProvisionDocuments = useCallback(
    (): ProvisionDocOverrideInput[] =>
      selectedDocs.map((doc) => {
        const draft = drafts[docId(doc)];
        const input: ProvisionDocOverrideInput = { pandadocId: docId(doc) };
        const changed = (value: string, original?: string | null) =>
          value.trim() && value.trim() !== (original ?? '').trim();

        if (changed(draft.firstName, doc.firstName)) {
          input.firstName = draft.firstName.trim();
        }
        if (changed(draft.lastName, doc.lastName)) {
          input.lastName = draft.lastName.trim();
        }
        if (draft.middleName.trim()) {
          input.middleName = draft.middleName.trim();
        }
        if (changed(draft.email, doc.email)) {
          input.email = draft.email.trim();
        }
        // Phone is required — always send it, normalised to +48 form.
        if (draft.phone.trim()) {
          input.phone = normalizePhone(draft.phone);
        }
        return input;
      }),
    [selectedDocs, drafts],
  );

  // Provision selected students into a group that already exists.
  const onProvision = useCallback(() => {
    const errors = validate();
    setValidationErrors(errors);
    setShowInvalid(true);
    if (errors.length || !folder?.existingGroupId) {
      return;
    }
    provision({
      groupId: folder.existingGroupId,
      contributionAmount: Number(contributionAmount),
      currency,
      documents: buildProvisionDocuments(),
    });
  }, [
    validate,
    folder,
    contributionAmount,
    currency,
    provision,
    buildProvisionDocuments,
  ]);

  // Folder has no group yet: create it, then auto-provision the students.
  // The payload is captured now; the table is behind the modal and can't be
  // edited meanwhile. The deferred run fires once the group appears (below).
  const pendingProvision = useRef<{
    documents: ProvisionDocOverrideInput[];
    contributionAmount: number;
    currency: string;
  } | null>(null);

  const onCreateGroupAndProvision = useCallback(() => {
    const errors = validate();
    setValidationErrors(errors);
    setShowInvalid(true);
    if (errors.length || !folder) {
      return;
    }
    pendingProvision.current = {
      documents: buildProvisionDocuments(),
      contributionAmount: Number(contributionAmount),
      currency,
    };
    showPopup(PopupNames.addGroup, {
      prefillName: folder.folderName ?? undefined,
      prefillCourseId: folder.detectedCourseId ?? undefined,
    });
  }, [
    validate,
    folder,
    contributionAmount,
    currency,
    buildProvisionDocuments,
    showPopup,
  ]);

  // Once the freshly created group is picked up by a refetch, run the
  // deferred provisioning automatically.
  useEffect(() => {
    if (pendingProvision.current && folder?.existingGroupId) {
      const payload = pendingProvision.current;
      pendingProvision.current = null;
      provision({ groupId: folder.existingGroupId, ...payload });
    }
  }, [folder?.existingGroupId, provision]);

  // Group was created but never matched the folder — warn instead of silently
  // dropping the deferred provisioning.
  const wasFetching = useRef(false);
  useEffect(() => {
    if (
      wasFetching.current &&
      !fetching &&
      pendingProvision.current &&
      !folder?.existingGroupId
    ) {
      pendingProvision.current = null;
      addToast({
        data: {
          title: t('pages.pandadocProvisioning.toasts.groupNotFound'),
          type: ToastTypes.warning,
        },
        type: GlobalToastTypeNames.Provisioning,
      });
    }
    wasFetching.current = fetching;
  }, [fetching, folder?.existingGroupId, addToast, t]);

  const docNameById = useMemo(() => {
    const map: Record<string, string> = {};
    docs.forEach((doc) => {
      const draft = drafts[docId(doc)];
      const first = draft?.firstName ?? doc.firstName ?? '';
      const last = draft?.lastName ?? doc.lastName ?? '';
      map[docId(doc)] = `${last} ${first}`.trim() || doc.name || docId(doc);
    });
    return map;
  }, [docs, drafts]);

  if (permissionDenied) {
    return (
      <S.EmptyState>{t('pages.pandadocProvisioning.noAccess')}</S.EmptyState>
    );
  }

  if (loading && !folder) {
    return (
      <S.CenterLoader>
        <LoadingIndicator />
      </S.CenterLoader>
    );
  }

  if (!folder) {
    return (
      <>
        <S.BackLink type="button" onClick={goBack}>
          {t('pages.pandadocProvisioning.folder.back')}
        </S.BackLink>
        <S.EmptyState>
          {t('pages.pandadocProvisioning.folder.notFound')}
        </S.EmptyState>
      </>
    );
  }

  const hasGroup = Boolean(folder.existingGroupId);

  return (
    <>
      <S.BackLink type="button" onClick={goBack}>
        {t('pages.pandadocProvisioning.folder.back')}
      </S.BackLink>

      <S.HeaderRow>
        <S.PageTitle>
          {folder.folderName ||
            t('pages.pandadocProvisioning.folder.defaultTitle')}
        </S.PageTitle>
        {hasGroup ? (
          <S.Badge variant="ok">
            {t('pages.pandadocProvisioning.folder.groupExists')}
            {folder.existingGroupName ? `: ${folder.existingGroupName}` : ''}
          </S.Badge>
        ) : (
          <S.Badge variant="neutral">
            {t('pages.pandadocProvisioning.folder.groupMissing')}
          </S.Badge>
        )}
      </S.HeaderRow>
      <S.SubTitle>
        {t('pages.pandadocProvisioning.folder.subtitle', {
          course:
            folder.detectedCourseName ||
            t('pages.pandadocProvisioning.folder.courseUndetected'),
          count: docs.length,
        })}
      </S.SubTitle>

      {!docs.length && (
        <S.EmptyState>
          {t('pages.pandadocProvisioning.folder.emptyDocs')}
        </S.EmptyState>
      )}

      {Boolean(docs.length) && (
        <S.Card>
          <S.Table>
            <thead>
              <tr>
                <th
                  aria-label={t('pages.pandadocProvisioning.folder.selectAria')}
                />
                <th>{t('pages.pandadocProvisioning.folder.headerName')}</th>
                <th>{t('pages.pandadocProvisioning.folder.headerEmail')}</th>
                <th>{t('pages.pandadocProvisioning.folder.headerPhone')}</th>
                <th>{t('pages.pandadocProvisioning.folder.headerContract')}</th>
                <th>{t('pages.pandadocProvisioning.folder.headerStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => {
                const id = docId(doc);
                const draft = drafts[id];
                if (!draft) {
                  return null;
                }
                const locked = Boolean(doc.alreadyProvisioned);
                const dataPending = !doc.firstName && !doc.email;

                return (
                  <S.TableRow key={id} dim={locked}>
                    <td>
                      <S.Checkbox
                        type="checkbox"
                        checked={draft.selected}
                        disabled={locked}
                        onChange={(event) =>
                          patchDraft(id, { selected: event.target.checked })
                        }
                      />
                    </td>
                    <td>
                      <S.NameCellGroup>
                        <S.CellInput
                          value={draft.lastName}
                          placeholder={t(
                            'pages.pandadocProvisioning.folder.placeholderLastName',
                          )}
                          disabled={locked}
                          invalid={showInvalid && invalidNameIds.has(id)}
                          onChange={(event) =>
                            patchDraft(id, { lastName: event.target.value })
                          }
                        />
                        <S.CellInput
                          value={draft.firstName}
                          placeholder={t(
                            'pages.pandadocProvisioning.folder.placeholderFirstName',
                          )}
                          invalid={showInvalid && invalidNameIds.has(id)}
                          disabled={locked}
                          onChange={(event) =>
                            patchDraft(id, { firstName: event.target.value })
                          }
                        />
                        <S.CellInput
                          value={draft.middleName}
                          placeholder={t(
                            'pages.pandadocProvisioning.folder.placeholderMiddleName',
                          )}
                          disabled={locked}
                          onChange={(event) =>
                            patchDraft(id, { middleName: event.target.value })
                          }
                        />
                        {dataPending && (
                          <S.FieldHint>
                            {t('pages.pandadocProvisioning.folder.dataPending')}
                          </S.FieldHint>
                        )}
                      </S.NameCellGroup>
                    </td>
                    <td>
                      <S.CellInput
                        value={draft.email}
                        placeholder={t(
                          'pages.pandadocProvisioning.folder.placeholderEmail',
                        )}
                        disabled={locked}
                        invalid={showInvalid && invalidEmailIds.has(id)}
                        onChange={(event) =>
                          patchDraft(id, { email: event.target.value })
                        }
                      />
                    </td>
                    <td>
                      <S.CellInput
                        value={draft.phone}
                        placeholder={t(
                          'pages.pandadocProvisioning.folder.placeholderPhone',
                        )}
                        disabled={locked}
                        invalid={
                          doc.phoneMissing ||
                          (showInvalid && invalidPhoneIds.has(id))
                        }
                        onChange={(event) =>
                          patchDraft(id, { phone: event.target.value })
                        }
                      />
                      {doc.phoneMissing && !draft.phone.trim() && (
                        <S.FieldHint error>
                          {t('pages.pandadocProvisioning.folder.enterPhone')}
                        </S.FieldHint>
                      )}
                    </td>
                    <td>
                      {doc.contractUrl ? (
                        <S.ContractLink
                          href={doc.contractUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('pages.pandadocProvisioning.folder.openContract')}
                        </S.ContractLink>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <S.BadgeRow>
                        {doc.alreadyProvisioned && (
                          <S.Badge variant="ok">
                            {t(
                              'pages.pandadocProvisioning.folder.badgeProvisioned',
                            )}
                          </S.Badge>
                        )}
                        {doc.studentExists && (
                          <S.Badge variant="warn">
                            {t(
                              'pages.pandadocProvisioning.folder.badgeStudentExists',
                            )}
                          </S.Badge>
                        )}
                        {doc.userExists && !doc.studentExists && (
                          <S.Badge variant="info">
                            {t(
                              'pages.pandadocProvisioning.folder.badgeUserExists',
                            )}
                          </S.Badge>
                        )}
                      </S.BadgeRow>
                    </td>
                  </S.TableRow>
                );
              })}
            </tbody>
          </S.Table>

          <S.Controls>
            <S.ControlGroup>
              <S.ControlLabel>
                {t('pages.pandadocProvisioning.folder.contributionAmount')}
              </S.ControlLabel>
              <S.NumberInput
                type="number"
                min="0"
                step="0.01"
                value={contributionAmount}
                onChange={(event) => setContributionAmount(event.target.value)}
              />
            </S.ControlGroup>
            <S.ControlGroup>
              <S.ControlLabel>
                {t('pages.pandadocProvisioning.folder.currency')}
              </S.ControlLabel>
              <S.Select
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </S.Select>
            </S.ControlGroup>
          </S.Controls>

          {showInvalid && Boolean(validationErrors.length) && (
            <S.ValidationBanner>
              {validationErrors.join(' ')}
            </S.ValidationBanner>
          )}

          <S.ActionsRow>
            {!hasGroup && (
              <S.PrimaryButton
                type="button"
                disabled={provisioning || fetching || !selectedDocs.length}
                onClick={onCreateGroupAndProvision}
              >
                {provisioning
                  ? t('pages.pandadocProvisioning.buttons.provisioning')
                  : t(
                      'pages.pandadocProvisioning.buttons.createGroupAndProvision',
                    )}
              </S.PrimaryButton>
            )}
            {hasGroup && (
              <S.PrimaryButton
                type="button"
                disabled={provisioning || !selectedDocs.length}
                onClick={onProvision}
              >
                {provisioning
                  ? t('pages.pandadocProvisioning.buttons.addingShort')
                  : t('pages.pandadocProvisioning.buttons.addToGroup')}
              </S.PrimaryButton>
            )}
          </S.ActionsRow>

          <ProvisioningResult
            results={results}
            topLevelErrors={topLevelErrors}
            docNameById={docNameById}
          />
        </S.Card>
      )}
    </>
  );
});
